import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { kebabCase } from 'lodash';
import { humanizeTenantId } from '@a0/support-center-common/lib/tenants';

import ApplianceCloudSelect from '../ApplianceCloudSelect';
import ApplianceStatusNotification from '../ApplianceStatusNotification';
import Auth0Notification from '../Auth0Notification';
import ReportDetailsTab from '../ReportDetailsTab';
import ReportLineChart from '../ReportLineChart';
import HotjarMasked from '../HotjarMasked';

import noop from '../../utils/noop';
import getReportDescription from '../../utils/getReportDescription';
import { getMonthNameWithPrefix } from '../../utils/dateUtility';
import { REPORT_REFRESH_24HRS } from '../../utils/resources/reports';

import './styles.css';

const zeroRecords = records =>
  (records && records.length <= 0) || records == null;
const itDoesnotHaveApplianceQuotaDetails = charts =>
  charts.length >= 1 &&
  charts[0].currentQuotaDetails &&
  !charts[0].currentQuotaDetails.appliance;
const itDoesnotHaveCloudQuotaDetails = charts =>
  charts.length >= 1 &&
  charts[0].currentQuotaDetails &&
  !charts[0].currentQuotaDetails.cloud;

class ReportDetails extends Component {
  constructor(props) {
    super(props);

    const { isAppliance, applianceRecords, records, charts } = props;
    const zeroAppliancesRecords = zeroRecords(applianceRecords);
    const zeroCloudRecords = zeroRecords(records);
    const notApplianceQuota = itDoesnotHaveApplianceQuotaDetails(charts);
    const notCloudQuota = itDoesnotHaveCloudQuotaDetails(charts);

    this.state = {
      applianceStatusModalShown: false,
      activeTable:
        isAppliance &&
        (!zeroAppliancesRecords ||
          !notApplianceQuota ||
          notCloudQuota ||
          zeroCloudRecords)
          ? 'appliance'
          : 'cloud',
      activeTableHasBeenSet: false
    };
    this.setActiveTable = this.setActiveTable.bind(this);
    this.showApplianceStatusModal = this.showApplianceStatusModal.bind(this);
    this.hideApplianceStatusModal = this.hideApplianceStatusModal.bind(this);
  }

  UNSAFE_componentWillReceiveProps({ records, applianceRecords, charts }) {
    const zeroAppliancesRecords = zeroRecords(applianceRecords);
    const zeroCloudRecords = zeroRecords(records);
    const notApplianceQuota = itDoesnotHaveApplianceQuotaDetails(charts);
    const notCloudQuota = itDoesnotHaveCloudQuotaDetails(charts);

    if (
      !this.state.activeTableHasBeenSet &&
      this.props.isAppliance &&
      !zeroCloudRecords &&
      !notCloudQuota &&
      (notApplianceQuota || zeroAppliancesRecords)
    ) {
      this.setActiveTable('cloud');
      this.setState({ activeTableHasBeenSet: true });
    }
  }

  getDescription() {
    return getReportDescription(this.props);
  }

  setActiveTable(activeTable) {
    this.setState({ activeTable });
  }

  isApplianceActive() {
    const { activeTable } = this.state;
    return activeTable === 'appliance';
  }

  isCloudActive() {
    const { activeTable } = this.state;
    return activeTable === 'cloud';
  }

  showApplianceStatusModal() {
    this.setState({
      applianceStatusModalShown: true
    });
  }

  hideApplianceStatusModal() {
    this.setState({
      applianceStatusModalShown: false
    });
  }

  onTabEnter(tab) {
    const filter = tab.filter || this.props.filter;
    const resetFilter = () => filter()(); // filter is meant to return a function, calling filter with empty args to reset filter, creating new reset function for readability
    const onEnter = tab.onEntering || this.props.onEntering;

    return () => {
      onEnter && onEnter(tab.selector);
      filter && resetFilter();
    };
  }

  onTabExit(tab) {
    const paginate = tab.paginate || this.props.paginate;
    return () => {
      paginate && paginate();
    };
  }

  renderDetailsHeader() {
    const { isAppliance } = this.props;

    if (isAppliance) {
      return (
        <ApplianceCloudSelect
          handleSelected={this.setActiveTable}
          currentSelected={this.state.activeTable}
        />
      );
    }

    return <h2>Historical data</h2>;
  }

  renderApplianceTabs() {
    const {
      isAppliance,
      applianceTabs,
      applianceActiveTab,
      applianceRecords,
      paginate,
      activePage,
      filter,
      filtering,
      onDownloadCSVClick,
    } = this.props;

    const noRecordsMessage =
      (applianceRecords && applianceRecords.length === 0) || !applianceRecords
        ? 'No PSaaS Appliances are currently sending data to the Auth0 Gateway.'
        : 'There are no records at this time.';

    if (
      isAppliance &&
      applianceTabs &&
      applianceTabs.length > 0
    ) {
      return (
        <div 
          id="report-details-tabs-appliance" 
          className={classNames('report-details-tabs', {
            'is-active': this.isApplianceActive()
          })}
        >
          <Tabs defaultActiveKey={applianceActiveTab}>
            {applianceTabs
              .map(({title, ...tab}) => {
                return (
                  <Tab
                    key={title}
                    eventKey={kebabCase(title)}
                    title={title}
                    onEntering={this.onTabEnter(tab)}
                    onExiting={this.onTabExit(tab)}
                    unmountOnExit
                  >
                    <ReportDetailsTab
                      {...tab}
                      records={applianceRecords}
                      paginate={paginate}
                      activePage={activePage}
                      filter={filter}
                      filtering={filtering}
                      noRecordsMessage={noRecordsMessage}
                      onDownloadCSVClick={onDownloadCSVClick}
                    />
                  </Tab>
                );
            })}
          </Tabs>
        </div>
      );
    }

    return null;
  }

  render() {
    const {
      title,
      tenantId,
      accountName,
      records,
      tabs,
      extendDefaultTabs,
      activeTab,
      paginate,
      activePage,
      filter,
      filtering,
      charts,
      handleClickBackToAllReports,
      quantityName,
      contactSales,
      contactSalesLink,
      isAppliance,
      isV8Plan,
      applianceUsageRecords,
      applianceUsageDueDate,
      month,
      contactSupport,
      onDownloadCSVClick,
      onEntering
    } = this.props;

    const { applianceStatusModalShown } = this.state;

    const _tabs = extendDefaultTabs ? [...tabs, ...extendDefaultTabs] : tabs;

    return (
      <div className="report-details">
        <div className="report-details-header">
          <h2 className="report-details-title">
            <strong>{title}</strong>
          </h2>
          <button
            className="btn btn-sm btn-transparent"
            onClick={handleClickBackToAllReports}
          >
            Back to all reports
          </button>
        </div>

        {!isV8Plan &&
          (<p className="report-details-subtitle">
            {(accountName || tenantId) ? (
              <span>
                for{' '}
                <HotjarMasked isInline>{accountName || humanizeTenantId(tenantId)}</HotjarMasked>
              </span>
            ) : null}
          </p>
          )
        }
        <p
          className="report-details-description"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: this.getDescription() }}
        />
        {isAppliance ? (
          <ApplianceStatusNotification
            applianceUsageRecords={applianceUsageRecords}
            applianceUsageDueDate={applianceUsageDueDate}
            isModalShown={applianceStatusModalShown}
            showModal={this.showApplianceStatusModal}
            hideModal={this.hideApplianceStatusModal}
            showOutdatedAlert
          />
        ) : (
          <Auth0Notification type="primary">
            {REPORT_REFRESH_24HRS}
          </Auth0Notification>
        )}
        {charts && charts.length >= 1 ? (
          <div className="report-details-charts">
            {charts.map(chart => (
              <ReportLineChart
                key={chart.type}
                {...chart}
                prefixedMonth={getMonthNameWithPrefix(month)}
                quantityName={quantityName}
                contactSales={contactSales}
                contactSalesLink={contactSalesLink}
                isAppliance={isAppliance}
                applianceUsageRecords={applianceUsageRecords}
                applianceUsageDueDate={applianceUsageDueDate}
                isModalShown={applianceStatusModalShown}
                showModal={this.showApplianceStatusModal}
                hideModal={this.hideApplianceStatusModal}
              />
            ))}
          </div>
        ) : null}
        <div className="report-details-content">
          {this.renderDetailsHeader()}
          {this.renderApplianceTabs()}
          <div 
            id="report-details-tabs-cloud" 
            className={classNames('report-details-tabs', {
              'is-active': this.isCloudActive()
            })}
          >
            <Tabs defaultActiveKey={activeTab}>
              {_tabs
                .map(({ title, ...tab}) => {
                  return (
                    <Tab
                      key={title}
                      eventKey={kebabCase(title)}
                      title={title}
                      onEntering={this.onTabEnter(tab)}
                      onExiting={this.onTabExit(tab)}
                      unmountOnExit
                    >
                      <ReportDetailsTab
                        {...tab}
                        records={tab.records || records}
                        paginate={tab.paginate || paginate}
                        activePage={tab.activePage || activePage}
                        filter={tab.filter || filter}
                        filtering={tab.filtering || filtering}
                        contactSupport={contactSupport}
                        onDownloadCSVClick={onDownloadCSVClick}
                      />
                    </Tab>
                  );
                })}
            </Tabs>
          </div>
        </div>
      </div>
    );
  }
}

ReportDetails.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  descriptionForAppliance: PropTypes.string,
  descriptionForMixedPlan: PropTypes.string,
  paginate: PropTypes.func.isRequired,
  activePage: PropTypes.number.isRequired,
  filtering: PropTypes.object.isRequired,
  filter: PropTypes.func.isRequired,
  tabs: PropTypes.array.isRequired,
  handleClickBackToAllReports: PropTypes.func.isRequired,
  records: PropTypes.array,
  applianceRecords: PropTypes.array,
  applianceTabs: PropTypes.array,
  charts: PropTypes.array,
  quantityName: PropTypes.string,
  contactSales: PropTypes.func,
  contactSalesLink: PropTypes.string,
  tenantId: PropTypes.string,
  accountName: PropTypes.string,
  isAppliance: PropTypes.bool,
  applianceUsageRecords: PropTypes.array,
  applianceUsageDueDate: PropTypes.string,
  activeTab: PropTypes.string,
  applianceActiveTab: PropTypes.string,
  month: PropTypes.string,
  descriptionAppliance: PropTypes.string,
  isMixedPlan: PropTypes.bool,
  extendDefaultTabs: PropTypes.array,
  contactSupport: PropTypes.element,
  onDownloadCSVClick: PropTypes.func,
  onEntering: PropTypes.func,
};

ReportDetails.defaultProps = {
  paginate: noop,
  activePage: 1,
  filtering: {},
  filter: noop,
  tabs: [],
  charts: [],
  handleClickBackToAllReports: noop
};

export default ReportDetails;
